import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './SearchDropdown.scss';

interface SearchDropdownProps<T> {
  items: T[];
  selected: T | null;
  setSelected: (selected: T) => void;
  displayProperty: keyof T;
  idProperty: keyof T;
  placeholder?: string;
  dropdownClassName?: string;
  toggleClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
  disabled?: boolean;
}

const SearchDropdown = <T extends Record<string, any>>({
  items,
  selected,
  setSelected,
  displayProperty,
  idProperty,
  placeholder = 'Seleccione una opción',
  dropdownClassName,
  disabled = false,
}: SearchDropdownProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState<T[]>(items);
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(items);
    } else {
      const filtered = items.filter((item) =>
        item[displayProperty].toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, items, displayProperty]);

  // Position the portal menu relative to the toggle button
  const updatePosition = useCallback(() => {
    if (!toggleRef.current) return;
    const rect = toggleRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 160),
      zIndex: 999999,
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      // Reposition on scroll/resize
      const onReposition = () => updatePosition();
      window.addEventListener('scroll', onReposition, true);
      window.addEventListener('resize', onReposition);
      return () => {
        window.removeEventListener('scroll', onReposition, true);
        window.removeEventListener('resize', onReposition);
      };
    }
  }, [isOpen, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        toggleRef.current && !toggleRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (item: T) => {
    setSelected(item);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    if (!disabled) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(!isOpen);
    }
  };

  const menu = isOpen ? createPortal(
    <div
      ref={menuRef}
      className="search-dropdown-portal-menu"
      style={menuStyle}
    >
      <input
        type="text"
        placeholder="Buscar..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="portal-search-input"
        autoFocus
      />
      <div className="portal-menu-items">
        {filteredItems.map((item) => (
          <div
            key={item[idProperty]}
            className="portal-menu-item"
            onClick={() => !disabled && handleSelect(item)}
          >
            {item[displayProperty]}
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div className="portal-menu-item disabled">No se encontraron resultados</div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={dropdownClassName ?? 'search-dropdown'}>
      <button
        ref={toggleRef}
        className={`search-dropdown-toggle ${isOpen ? 'is-open' : ''}`}
        onClick={handleToggleClick}
        disabled={disabled}
        type="button"
      >
        <span className="toggle-text">
          {selected ? selected[displayProperty] : placeholder}
        </span>
        <span className={`toggle-chevron ${isOpen ? 'rotated' : ''}`} />
      </button>
      {menu}
    </div>
  );
};

export default SearchDropdown;
